import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { LoginResponse } from '../interfaces/login-response';
import { ToastService } from '../../shared/services/toast.service';
import { Router } from '@angular/router';
import { SharedService } from '../../shared/services/shared.service';
import { skipAuth, useSessionToken } from '../../core/auth-context';

const baseUrl = environment.trazeApi.host;
const apiUrl = environment.apiUrl;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private http = inject(HttpClient);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private sharedService = inject(SharedService);

  private _sessionToken = signal<string | null>(localStorage.getItem('sessionToken'));
  private _authToken = signal<string | null>(localStorage.getItem('authToken'));

  private _session = signal<any>(null);
  private _subscriptionImage = signal<string>(this.sharedService.noImage);
  private _allowedCustomerAccounts = [];

  customerAccounts = signal<any[]>([]);

  public sessionToken = this._sessionToken.asReadonly();
  public session = this._session.asReadonly();
  public authToken = this._authToken.asReadonly();

  user = computed<any>(() => this._session()?.user);
  appSubscription = computed<any>(() => this._session()?.appSubscription);
  appSubscriptions = computed<any>(() => this._session()?.appSubscriptions);
  subscriptionImage = computed<any>(() => this._subscriptionImage);

  login(userName: string, password: string): Observable<boolean> {

    const appSubscriptionId = localStorage.getItem('appSubscriptionId') || 'ec528474-e444-11e8-88c5-b39ca7534a35';

    let headers = new HttpHeaders();
    headers = headers.set('appSubscriptionId', appSubscriptionId);

    const context = new HttpContext().set(skipAuth, true);

    return this.http.post<LoginResponse>(`${apiUrl}/security/login`,
      {
        userName: userName,
        password: password,
        application: 'webshop'
      },
      { headers, context }
    ).pipe(
      tap(res => {
        this._sessionToken.set(res.token);
        this._authToken.set(res.authorizationToken);
        localStorage.setItem('sessionToken', res.token);
        localStorage.setItem('authToken', res.authorizationToken);
      }),
      switchMap(() => this.getSession()),
      map(() => true),
      catchError((error: any) => this.handleAuthError(error))
    );
  }

  getSession(): Observable<any> {

    const context = new HttpContext().set(useSessionToken, true);

    return this.http.post(`${apiUrl}/security/getSession`, {}, { context }).pipe(
      tap((session: any) => {

        if (!session.user.isAppUserAdmin) {
          this._allowedCustomerAccounts = (session.permissions || []).filter((o: any) => o.type === 'customerAccounts' && o.value).map((o: any) => o.value);
        }

        localStorage.setItem('appSubscriptionId', session.appSubscription.id);
        this._session.set(session);
      }),
      switchMap(() => this.getSubscriptionImage()),
      switchMap(() => this.getCustomerAccounts())
    );
  }

  changeSessionSubscription(appSubscriptionId: string): Observable<any> {

    return this.http.post(`${baseUrl}/security/changeSessionSubscription`, {
      id: appSubscriptionId
    }).pipe(
      tap((res: any) => {
        localStorage.setItem('appSubscriptionId', res.id);
      })
    );
  }

  isLoggedIn(): Observable<boolean> {

    const sessionToken = localStorage.getItem('sessionToken');
    const authToken = localStorage.getItem('authToken');

    if (!sessionToken || !authToken) {
      return this.handleAuthError({});
    }

    if (this._session()) {
      return of(true);
    }

    return this.getSession().pipe(
      map(() => true),
      catchError((error: any) => this.handleAuthError(error))
    );
  }

  private handleAuthError(error: any) {

    this.logout();
    return of(false);
  }

  logout() {

    this._sessionToken.set(null);
    this._session.set(null);
    this._authToken.set(null);
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('authToken');
    this.router.navigateByUrl('auth');
  }

  getSubscriptionImage(): Observable<any> {

    const companyId = this.appSubscription().company.id;

    return this.sharedService.getImages([companyId]).pipe(
      tap((res: any) => {
        const image = res?.find((o: any) => o.id === companyId)?.image;;
        this._subscriptionImage = image;
      })
    );
  }

  getCustomerAccounts(): Observable<any> {

    const where: any = {};

    if (this.user().isAppUserAdmin) {
      where.type = 'customer';
      where.isActive = true;
    }
    else {
      where.id = this._allowedCustomerAccounts;
    }

    return this.http.post(`${baseUrl}/trading/accounts`, {
      where: where,
      order: [['name', 'ASC']]
    }).pipe(
      tap((res: any) => {

        this.customerAccounts.set(res);
      })
    );
  }
}
